import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import Page from "./Page";
import useData from "../../hooks/useData";

const emptyDraft = {
  name: "",
  name_fr: "",
  category: "",
  category_fr: "",
  linkUrl: "",
  icon: "",
  isActive: true,
  secured: false,
  sortOrder: 0,
  categorySortOrder: 0,
};

const PublicLinksAdmin = () => {
  const { getListFromServer, updateRecord, deleteRecord } = useData();

  const normalizeLinkUrl = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (/^(https?:|mailto:|tel:)/i.test(raw)) return raw;
    return `https://${raw}`;
  };

  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState(emptyDraft);
  const [editingId, setEditingId] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const loadRows = async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const list = await getListFromServer("PublicLink", {});
      const safeList = Array.isArray(list) ? list : [];
      safeList.sort((a, b) => {
        const ac = Number(a.categorySortOrder || 0);
        const bc = Number(b.categorySortOrder || 0);
        if (ac !== bc) return ac - bc;
        const aCat = String(a.category || "");
        const bCat = String(b.category || "");
        if (aCat !== bCat) return aCat.localeCompare(bCat);
        const as = Number(a.sortOrder || 0);
        const bs = Number(b.sortOrder || 0);
        if (as !== bs) return as - bs;
        return String(a.name || "").localeCompare(String(b.name || ""));
      });
      setRows(safeList);
    } catch (err) {
      setRows([]);
      setErrorMessage(err.message || "Failed to load links");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  const setField = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const resetForm = () => {
    setDraft(emptyDraft);
    setEditingId("");
    setErrorMessage("");
  };

  const editRow = (row) => {
    setEditingId(row.id || "");
    setDraft({
      name: row.name || "",
      name_fr: row.name_fr || "",
      category: row.category || "",
      category_fr: row.category_fr || "",
      linkUrl: row.linkUrl || "",
      icon: row.icon || "",
      isActive: row.isActive !== false,
      secured: row.secured === true,
      sortOrder: Number(row.sortOrder || 0),
      categorySortOrder: Number(row.categorySortOrder || 0),
    });
  };

  const saveDraft = async () => {
    setErrorMessage("");

    if (!String(draft.name || "").trim()) {
      setErrorMessage("Name is required");
      return;
    }
    if (!String(draft.category || "").trim()) {
      setErrorMessage("Category is required");
      return;
    }
    if (!String(draft.linkUrl || "").trim()) {
      setErrorMessage("Link URL is required");
      return;
    }

    const normalizedUrl = normalizeLinkUrl(draft.linkUrl);
    try {
      // Validate URL format for external links while allowing mailto/tel.
      if (!/^mailto:|^tel:/i.test(normalizedUrl)) {
        // eslint-disable-next-line no-new
        new URL(normalizedUrl);
      }
    } catch {
      setErrorMessage("Link URL is invalid");
      return;
    }

    const payload = {
      ...(editingId ? { id: editingId } : {}),
      ...draft,
      linkUrl: normalizedUrl,
      sortOrder: Number(draft.sortOrder || 0),
      categorySortOrder: Number(draft.categorySortOrder || 0),
    };

    const result = await updateRecord("PublicLink", payload, true);
    if (!result?.isSuccessful) {
      setErrorMessage(result?.message || "Failed to save link");
      return;
    }

    resetForm();
    await loadRows();
  };

  const removeRow = async (row) => {
    const id = row?.id;
    if (!id) return;
    await deleteRecord("PublicLink", id);
    if (editingId === id) {
      resetForm();
    }
    await loadRows();
  };

  return (
    <Page pageName="Public Links Admin">
      <Stack spacing={2}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="h5">Public Links Admin</Typography>
          <Link
            href="/links"
            target="_blank"
            rel="noopener noreferrer"
          >
            Open Public Page
          </Link>
        </Box>

        {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

        <Paper
          variant="outlined"
          sx={{ p: 2 }}
        >
          <Stack spacing={1.5}>
            <Typography variant="h6">
              {editingId ? "Edit Link" : "Add Link"}
            </Typography>
            <Stack
              direction="row"
              spacing={1.5}
              flexWrap="wrap"
            >
              <TextField
                label="Name"
                value={draft.name}
                onChange={(e) => setField("name", e.target.value)}
                sx={{ minWidth: 240, flex: 1 }}
              />
              <TextField
                label="Name (FR)"
                value={draft.name_fr}
                onChange={(e) => setField("name_fr", e.target.value)}
                sx={{ minWidth: 240, flex: 1 }}
              />
            </Stack>
            <Stack
              direction="row"
              spacing={1.5}
              flexWrap="wrap"
            >
              <TextField
                label="Category"
                value={draft.category}
                onChange={(e) => setField("category", e.target.value)}
                sx={{ minWidth: 240, flex: 1 }}
              />
              <TextField
                label="Category (FR)"
                value={draft.category_fr}
                onChange={(e) => setField("category_fr", e.target.value)}
                sx={{ minWidth: 240, flex: 1 }}
              />
            </Stack>
            <Stack
              direction="row"
              spacing={1.5}
              flexWrap="wrap"
            >
              <TextField
                label="Link URL"
                value={draft.linkUrl}
                onChange={(e) => setField("linkUrl", e.target.value)}
                sx={{ minWidth: 360, flex: 2 }}
              />
              <TextField
                label="Icon"
                value={draft.icon}
                onChange={(e) => setField("icon", e.target.value)}
                sx={{ minWidth: 220, flex: 1 }}
              />
            </Stack>
            <Stack
              direction="row"
              spacing={1.5}
              flexWrap="wrap"
              alignItems="center"
            >
              <TextField
                label="Category Order"
                type="number"
                value={draft.categorySortOrder}
                onChange={(e) => setField("categorySortOrder", e.target.value)}
                sx={{ width: 180 }}
              />
              <TextField
                label="Link Order"
                type="number"
                value={draft.sortOrder}
                onChange={(e) => setField("sortOrder", e.target.value)}
                sx={{ width: 140 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={draft.isActive}
                    onChange={(e) => setField("isActive", e.target.checked)}
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={draft.secured === true}
                    onChange={(e) => setField("secured", e.target.checked)}
                  />
                }
                label="Secured"
              />
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              justifyContent="flex-end"
            >
              {editingId ? (
                <Button
                  variant="outlined"
                  onClick={resetForm}
                >
                  Cancel
                </Button>
              ) : null}
              <Button
                variant="contained"
                onClick={saveDraft}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>URL</TableCell>
                  <TableCell>Icon</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Secured</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.category || "-"}</TableCell>
                    <TableCell>{row.name || "-"}</TableCell>
                    <TableCell>{row.linkUrl || "-"}</TableCell>
                    <TableCell>{row.icon || "-"}</TableCell>
                    <TableCell>{`${Number(row.categorySortOrder || 0)}/${Number(row.sortOrder || 0)}`}</TableCell>
                    <TableCell>
                      {row.isActive ? "Active" : "Inactive"}
                    </TableCell>
                    <TableCell>{row.secured ? "Yes" : "No"}</TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => editRow(row)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => removeRow(row)}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      align="center"
                    >
                      No links found
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Stack>
    </Page>
  );
};

export default PublicLinksAdmin;
